import { initializeApp, FirebaseApp } from "firebase/app";
import { getFunctions, Functions, connectFunctionsEmulator } from "firebase/functions";
import { getAnalytics, Analytics, isSupported as isAnalyticsSupported, setAnalyticsCollectionEnabled } from "firebase/analytics"; 
import { AppCheck, initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

let app: FirebaseApp | null = null;
let functions: Functions | null = null;
let analytics: Analytics | null = null;
export let appCheck: AppCheck | null = null;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID, 
};
const isDevelopment = import.meta.env.MODE === "development";
const enableAppCheckInDev = import.meta.env.VITE_ENABLE_APPCHECK_DEV === "true";
const enableAppCheckInProd = import.meta.env.VITE_ENABLE_APPCHECK !== "false";
const shouldInitializeAppCheck = isDevelopment ? enableAppCheckInDev : enableAppCheckInProd;
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

async function initializeFirebase() {
  if (app) return; // Already initialized

  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.error("Firebase API Key or Project ID is missing for marketing site.");
    return;
  }

  try {
    app = initializeApp(firebaseConfig);

    if (shouldInitializeAppCheck && recaptchaSiteKey) {
      if (isDevelopment) {
        const debugToken = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;
        (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken || true;
      }
      try {
        appCheck = initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
          isTokenAutoRefreshEnabled: true,
        });
      } catch (error) {
        console.warn('[Marketing] App Check initialization failed.', error);
        appCheck = null;
      }
    } else if (shouldInitializeAppCheck && !recaptchaSiteKey) {
      console.warn("[Marketing] App Check skipped: VITE_RECAPTCHA_SITE_KEY is missing.");
    }
    
    // Initialize Functions with specific region if needed (default is usually us-central1)
    // Note: If your functions are in a different region, pass it here: getFunctions(app, 'europe-west1')
    functions = getFunctions(app, import.meta.env.VITE_FIREBASE_REGION || 'us-central1');

    // --- EMULATOR CONNECTION LOGIC ---
    // Connect to emulators if in development mode or if specific env vars are set
    if (import.meta.env.MODE === 'development' || import.meta.env.VITE_EMULATOR_FUNCTIONS_HOST) {
        console.log("[Marketing] Attempting to connect to Firebase Functions emulator...");
        const functionsHost = import.meta.env.VITE_EMULATOR_FUNCTIONS_HOST || 'localhost';
        const functionsPort = parseInt(import.meta.env.VITE_EMULATOR_FUNCTIONS_PORT || '5000', 10);
        
        try {
            connectFunctionsEmulator(functions, functionsHost, functionsPort);
            console.log(`[Marketing] Connected to Functions emulator at ${functionsHost}:${functionsPort}`);
        } catch (e) {
            console.error("[Marketing] Failed to connect to Functions emulator:", e);
        }
    }
    // ---------------------------------

    if (firebaseConfig.measurementId && (await isAnalyticsSupported())) {
      analytics = getAnalytics(app);
      setAnalyticsCollectionEnabled(analytics, false);
      console.log("[marketing/src/config/firebase.ts] Firebase Analytics initialized.");
    } else {
      console.log("[marketing/src/config/firebase.ts] Firebase Analytics not initialized.");
    }
  } catch (error) {
    console.error("Error initializing Firebase:", error);
    app = null;
    functions = null;
    analytics = null;
    appCheck = null;
  }
}

// Export a function that returns the services, initializing if necessary.
export async function getFirebaseServices() {
  if (!app) {
    await initializeFirebase();
  }
  return { app, functions, analytics, appCheck };
}
